import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import Seo from "./Seo";
import { HERO_LOGO_URL, TAGLINES, PRESS_CONTACT_EMAIL, SOCIAL } from "./constants";
import { toast } from "sonner";
import { Download, Copy, Check, ChevronLeft, ExternalLink } from "lucide-react";

/** Brand color tokens — kept in sync with index.css for press kit consumers. */
const BRAND_COLORS = [
  { name: "Brand pink",   hex: "#D65A8C", note: "Primary accent — links, CTAs, headings." },
  { name: "Brand soft",   hex: "#FBE4EC", note: "Background washes, soft cards, hero gradient." },
  { name: "Brand deep",   hex: "#1B1F2E", note: "Wordmark + display headings." },
  { name: "Background",   hex: "#FFF7F3", note: "Warm white — primary surface." },
  { name: "Foreground",   hex: "#221E2A", note: "Body text — never pure black." },
];

const BOILERPLATE_SHORT =
  "Sister Signal is a private emotional safety companion designed to help " +
  "people recognise unhealthy relationship patterns, strengthen personal safety, " +
  "and make informed decisions with confidence. Available on iOS and the web, " +
  "with full English and Spanish support.";

const BOILERPLATE_LONG =
  "Sister Signal was created from lived experience. The platform combines a " +
  "trauma-informed educational library, AI-assisted message decoding, " +
  "nervous-system tools, a Trusted Circle with quiet check-ins, a Quick Exit " +
  "calculator disguise, and a financial-freedom toolkit — all wrapped in a " +
  "privacy-first design that lets users remain anonymous if they choose. " +
  "Sister Signal is iOS-native (App Store) and web-available, free at the " +
  "point of need, and bilingual (English and Spanish, es-MX).";

const FOUNDER_BIO =
  "Sister Signal was founded by a survivor of intimate partner abuse who, in " +
  "the aftermath, could not find a tool that felt safe enough to use. She " +
  "built the platform on the conviction that clarity is itself a form of " +
  "protection — and that survivors deserve technology made with the same " +
  "care they were not always shown.";

function CopyRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <div className="rounded-2xl bg-card border border-border p-5" data-testid={testid}>
      <p className="text-xs uppercase tracking-widest text-foreground/55">{label}</p>
      <p className="mt-2 text-foreground/85 leading-relaxed whitespace-pre-wrap">{value}</p>
      <button
        onClick={copy}
        data-testid={`${testid}-copy`}
        className="mt-3 inline-flex items-center gap-1 text-xs text-brand hover:underline"
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? "Copied" : "Copy to clipboard"}
      </button>
    </div>
  );
}

export default function MediaKit() {
  return (
    <MarketingShell>
      <Seo
        title="Media Kit"
        description="Sister Signal media kit — logo, brand colors, taglines, boilerplate, founder bio. Free for editorial use."
        path="/press/media-kit"
      />
      <article className="max-w-4xl mx-auto px-6 sm:px-10 py-20" data-testid="page-media-kit">
        <Link to="/press" className="inline-flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-6">
          <ChevronLeft className="w-4 h-4" /> Back to press
        </Link>
        <p className="font-script text-2xl text-brand">brand assets</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">Media Kit.</h1>
        <p className="mt-5 text-lg text-foreground/75 leading-relaxed">
          Everything you need to write or produce a piece on Sister Signal. Free for editorial use.
          For high-resolution assets, custom interviews, or anything not listed here, please email
          {" "}<a href={`mailto:${PRESS_CONTACT_EMAIL}`} className="text-brand hover:underline">{PRESS_CONTACT_EMAIL}</a>.
        </p>

        {/* LOGO ------------------------------------------------------------- */}
        <section className="mt-12">
          <p className="font-script text-xl text-brand">the mark</p>
          <h2 className="font-serif text-3xl mt-1">Logo.</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-5">
            <div className="rounded-3xl bg-brand-soft/60 border border-brand/15 p-6 grid place-items-center">
              <img src={HERO_LOGO_URL} alt="Sister Signal logo on blush" className="w-full max-h-72 object-contain" />
            </div>
            <div className="rounded-3xl bg-foreground p-6 grid place-items-center">
              <img src={HERO_LOGO_URL} alt="Sister Signal logo on dark" className="w-full max-h-72 object-contain" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href={HERO_LOGO_URL}
              download="sister-signal-logo.png"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="media-kit-download-logo"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white text-sm hover:bg-brand/90 transition-all"
            >
              <Download className="w-4 h-4" /> Download PNG
            </a>
            <a
              href={HERO_LOGO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-card border border-border text-sm hover:border-brand/40 transition-all"
            >
              <ExternalLink className="w-4 h-4" /> Open full-size
            </a>
          </div>
          <p className="mt-3 text-xs text-foreground/55">
            Please don't recolour, distort, or place text over the logo. Maintain at least 16px of clear space around it.
          </p>
        </section>

        {/* COLORS ----------------------------------------------------------- */}
        <section className="mt-12">
          <p className="font-script text-xl text-brand">the palette</p>
          <h2 className="font-serif text-3xl mt-1">Colors.</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            {BRAND_COLORS.map((c) => (
              <div key={c.hex} className="rounded-2xl bg-card border border-border overflow-hidden" data-testid={`color-${c.hex.slice(1).toLowerCase()}`}>
                <div className="h-20" style={{ backgroundColor: c.hex }} />
                <div className="p-5">
                  <p className="font-serif text-lg">{c.name}</p>
                  <p className="font-mono text-sm text-foreground/65 mt-0.5">{c.hex}</p>
                  <p className="mt-2 text-sm text-foreground/65 leading-relaxed">{c.note}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* TYPOGRAPHY ------------------------------------------------------- */}
        <section className="mt-12">
          <p className="font-script text-xl text-brand">the type</p>
          <h2 className="font-serif text-3xl mt-1">Typography.</h2>
          <div className="mt-5 grid sm:grid-cols-3 gap-4">
            <div className="rounded-2xl bg-card border border-border p-5">
              <p className="text-xs uppercase tracking-widest text-foreground/55">Display</p>
              <p className="font-serif text-3xl mt-2">Playfair Display</p>
              <p className="text-xs text-foreground/55 mt-1 font-mono">font-family: 'Playfair Display'</p>
            </div>
            <div className="rounded-2xl bg-card border border-border p-5">
              <p className="text-xs uppercase tracking-widest text-foreground/55">Body</p>
              <p className="text-xl mt-2">Inter (system)</p>
              <p className="text-xs text-foreground/55 mt-1 font-mono">font-family: ui-sans-serif</p>
            </div>
            <div className="rounded-2xl bg-card border border-border p-5">
              <p className="text-xs uppercase tracking-widest text-foreground/55">Script accent</p>
              <p className="font-script text-2xl mt-2">Pinyon Script</p>
              <p className="text-xs text-foreground/55 mt-1 font-mono">font-family: 'Pinyon Script'</p>
            </div>
          </div>
        </section>

        {/* TAGLINES + COPY -------------------------------------------------- */}
        <section className="mt-12">
          <p className="font-script text-xl text-brand">the voice</p>
          <h2 className="font-serif text-3xl mt-1">Copy & taglines.</h2>
          <div className="mt-5 space-y-4">
            <CopyRow label="Primary tagline"   value={TAGLINES.primary}        testid="copy-tagline-primary" />
            <CopyRow label="Secondary tagline" value={TAGLINES.secondary}      testid="copy-tagline-secondary" />
            <CopyRow label="Boilerplate (short, ~50 words)" value={BOILERPLATE_SHORT} testid="copy-boilerplate-short" />
            <CopyRow label="Boilerplate (long, ~110 words)" value={BOILERPLATE_LONG}  testid="copy-boilerplate-long" />
            <CopyRow label="Founder bio"        value={FOUNDER_BIO}             testid="copy-founder-bio" />
          </div>
        </section>

        {/* LINKS ------------------------------------------------------------ */}
        <section className="mt-12">
          <p className="font-script text-xl text-brand">essentials</p>
          <h2 className="font-serif text-3xl mt-1">Quick links.</h2>
          <ul className="mt-5 grid sm:grid-cols-2 gap-3">
            {[
              ["Website", "https://sistersignal.org"],
              ["App Store", SOCIAL.appStore],
              ["Instagram", SOCIAL.instagram],
              ["LinkedIn", SOCIAL.linkedin],
              ["TikTok", SOCIAL.tiktok],
              ["Facebook", SOCIAL.facebook],
            ].map(([label, url]) => (
              <li key={url} className="rounded-2xl bg-card border border-border p-4 flex items-center justify-between gap-3">
                <span className="text-sm">{label}</span>
                <a href={url} target="_blank" rel="noopener noreferrer"
                   className="text-sm text-brand hover:underline inline-flex items-center gap-1">
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* CONTACT ---------------------------------------------------------- */}
        <section className="mt-12 rounded-3xl bg-brand-soft/40 border border-brand/20 p-8" data-testid="media-kit-contact">
          <p className="font-script text-xl text-brand">need anything else?</p>
          <p className="mt-1 font-serif text-2xl leading-snug">
            We can send high-res files, screenshots, interview availability, or survivor introductions (with consent).
          </p>
          <a href={`mailto:${PRESS_CONTACT_EMAIL}`}
             className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all">
            Email {PRESS_CONTACT_EMAIL}
          </a>
        </section>
      </article>
    </MarketingShell>
  );
}
